import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _typeof from "@babel/runtime/helpers/esm/typeof";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import * as React from 'react';
import classNames from 'classnames';
import KeyCode from "rc-util/es/KeyCode";
import DatePanel from '../DatePanel';
import RangeContext from '../../RangeContext';
import { tuple } from '../../utils/miscUtil';
import { setDateTime as setTime } from '../../utils/timeUtil';
import { getDateInputMaxLength, getTimeInputMaxLength } from '../../utils/dateUtil';
import { getFormatValue } from '../../utils/uiUtil';
var ACTIVE_PANEL = tuple('date', 'time');

function DatetimePanel(props) {
  var _classNames2, _classNames3;

  var prefixCls = props.prefixCls,
      operationRef = props.operationRef,
      generateConfig = props.generateConfig,
      value = props.value,
      defaultValue = props.defaultValue,
      showTime = props.showTime,
      onSelect = props.onSelect,
      panelDateInputChange = props.panelDateInputChange,
      panelPrefixCls = props.panelPrefixCls,
      format = props.format;

  var _React$useState = React.useState(null),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      activePanel = _React$useState2[0],
      setActivePanel = _React$useState2[1];

  var _React$useState3 = React.useState(false),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      dateFocused = _React$useState4[0],
      setDateFocused = _React$useState4[1];

  var _React$useState5 = React.useState(false),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      timeFocused = _React$useState6[0],
      setTimeFocused = _React$useState6[1];

  var dateOperationRef = React.useRef({});
  var timeOperationRef = React.useRef({}); // 跟踪输入法组合状态，composition 期间不触发 panelDateInputChange

  var isComposingRef = React.useRef(false);
  var timeProps = _typeof(showTime) === 'object' ? _objectSpread({}, showTime) : {};

  var _React$useContext = React.useContext(RangeContext),
      inRange = _React$useContext.inRange,
      onTimeSelect = _React$useContext.onTimeSelect,
      rangedValue = _React$useContext.rangedValue,
      panelPosition = _React$useContext.panelPosition;

  var dateInputMaxLength = React.useMemo(function () {
    if (!format) {
      return undefined;
    }

    return getDateInputMaxLength(getFormatValue(format, generateConfig));
  }, [format, generateConfig]);
  var timeInputMaxLength = React.useMemo(function () {
    if (!format) {
      return undefined;
    }

    return getTimeInputMaxLength(getFormatValue(format, generateConfig));
  }, [format, generateConfig]); // ======================= Keyboard =======================

  function getNextActive(offset) {
    var activeIndex = ACTIVE_PANEL.indexOf(activePanel) + offset;
    var nextActivePanel = ACTIVE_PANEL[activeIndex] || null;
    return nextActivePanel;
  }

  var onBlur = function onBlur(e) {
    if (timeOperationRef.current.onBlur) {
      timeOperationRef.current.onBlur(e);
    }

    setActivePanel(null);
  };

  operationRef.current = {
    onKeyDown: function onKeyDown(event) {
      // Switch active panel
      if (event.which === KeyCode.TAB) {
        var nextActivePanel = getNextActive(event.shiftKey ? -1 : 1);
        setActivePanel(nextActivePanel);

        if (nextActivePanel) {
          event.preventDefault();
        }

        return true;
      } // Operate on current active panel


      if (activePanel) {
        var ref = activePanel === 'date' ? dateOperationRef : timeOperationRef;

        if (ref.current && ref.current.onKeyDown) {
          ref.current.onKeyDown(event);
        }

        return true;
      } // Switch first active panel if operate without panel


      if ([KeyCode.LEFT, KeyCode.RIGHT, KeyCode.UP, KeyCode.DOWN].includes(event.which)) {
        setActivePanel('date');
        return true;
      }

      return false;
    },
    onBlur: onBlur,
    onClose: onBlur
  }; // ======================== Events ========================

  var onInternalSelect = function onInternalSelect(date, source) {
    var selectedDate = date;

    if (source === 'date' && !value && timeProps.defaultValue) {
      // Date with time defaultValue
      selectedDate = generateConfig.setHour(selectedDate, generateConfig.getHour(timeProps.defaultValue));
      selectedDate = generateConfig.setMinute(selectedDate, generateConfig.getMinute(timeProps.defaultValue));
      selectedDate = generateConfig.setSecond(selectedDate, generateConfig.getSecond(timeProps.defaultValue));
    } else if (source === 'time' && !value && defaultValue) {
      selectedDate = generateConfig.setYear(selectedDate, generateConfig.getYear(defaultValue));
      selectedDate = generateConfig.setMonth(selectedDate, generateConfig.getMonth(defaultValue));
      selectedDate = generateConfig.setDate(selectedDate, generateConfig.getDate(defaultValue));
    } // 范围选择


    if (inRange) {
      if (onTimeSelect && source === 'time') {
        onTimeSelect(selectedDate);
      }

      if (onSelect && source === 'date') {
        onSelect(selectedDate, 'mouse');
      }
    } else {
      // 非范围选择
      if (value && source === 'time') {
        selectedDate = generateConfig.setYear(selectedDate, generateConfig.getYear(value));
        selectedDate = generateConfig.setMonth(selectedDate, generateConfig.getMonth(value));
        selectedDate = generateConfig.setDate(selectedDate, generateConfig.getDate(value));
      }

      onSelect(selectedDate, 'mouse');
    }
  }; // ======================== Render ========================
  // const disabledTimes = disabledTime ? disabledTime(value || null) : {};
  // 是否禁用


  var isDisabled = function isDisabled() {
    if (inRange) {
      return !(rangedValue === null || rangedValue === void 0 ? void 0 : rangedValue.every(function (v) {
        return !!v;
      }));
    } else {
      return !value;
    }
  }; // 单击1个日期时，开始与结束时间均显示该日期
  // const renderDateString = () => {
  //   if (inRange) {
  //     const dateValue = value || rangedValue?.filter((v) => !!v)[0];
  //     return dateValue
  //       ? generateConfig.locale.format(props.locale.locale, dateValue, 'YYYY-MM-DD')
  //       : '- -';
  //   } else {
  //     return value ? generateConfig.locale.format(props.locale.locale, value, 'YYYY-MM-DD') : '- -';
  //   }
  // };


  return /*#__PURE__*/React.createElement("div", {
    className: classNames(panelPrefixCls, _defineProperty({}, "".concat(panelPrefixCls, "-active"), activePanel))
  }, /*#__PURE__*/React.createElement(DatePanel, _extends({}, props, {
    operationRef: dateOperationRef,
    active: activePanel === 'date',
    onSelect: function onSelect(date) {
      onInternalSelect(setTime(generateConfig, date, showTime && _typeof(showTime) === 'object' ? showTime.defaultValue : null), 'date');
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: classNames("".concat(panelPrefixCls, "-wrap-datetime"))
  }, inRange && /*#__PURE__*/React.createElement("span", {
    className: "".concat(panelPrefixCls, "-wrap-datetime-range-label")
  }, panelPosition === 'left' ? props.locale.start : props.locale.end), !inRange && /*#__PURE__*/React.createElement("span", {
    className: "".concat(panelPrefixCls, "-wrap-datetime-label")
  }, props.locale.date), /*#__PURE__*/React.createElement("div", {
    className: classNames("".concat(panelPrefixCls, "-wrap-datetime-date"))
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames("".concat(panelPrefixCls, "-wrap-datetime-date-input ").concat(prefixCls, " ").concat(prefixCls, "-small"), (_classNames2 = {}, _defineProperty(_classNames2, "".concat(prefixCls, "-focused"), dateFocused), _defineProperty(_classNames2, "".concat(prefixCls, "-disabled"), isDisabled()), _classNames2))
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames("".concat(prefixCls, "-input"))
  }, /*#__PURE__*/React.createElement("input", {
    onFocus: function onFocus() {
      return setDateFocused(true);
    },
    onBlur: function onBlur() {
      return setDateFocused(false);
    },
    onCompositionStart: function onCompositionStart() {
      isComposingRef.current = true;
    },
    onCompositionEnd: function onCompositionEnd() {
      isComposingRef.current = false;
      panelDateInputChange();
    },
    onChange: function onChange() {
      if (isComposingRef.current) return;
      panelDateInputChange();
    },
    autoComplete: "off",
    disabled: isDisabled(),
    readOnly: isDisabled(),
    maxLength: dateInputMaxLength
  })))), /*#__PURE__*/React.createElement("div", {
    className: classNames("".concat(panelPrefixCls, "-wrap-datetime-time"))
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames("".concat(panelPrefixCls, "-wrap-datetime-time-input ").concat(prefixCls, " ").concat(prefixCls, "-small"), (_classNames3 = {}, _defineProperty(_classNames3, "".concat(prefixCls, "-focused"), timeFocused), _defineProperty(_classNames3, "".concat(prefixCls, "-disabled"), isDisabled()), _classNames3))
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames("".concat(prefixCls, "-input"))
  }, /*#__PURE__*/React.createElement("input", {
    onFocus: function onFocus() {
      return setTimeFocused(true);
    },
    onBlur: function onBlur() {
      return setTimeFocused(false);
    },
    onCompositionStart: function onCompositionStart() {
      isComposingRef.current = true;
    },
    onCompositionEnd: function onCompositionEnd() {
      isComposingRef.current = false;
      panelDateInputChange();
    },
    onChange: function onChange() {
      if (isComposingRef.current) return;
      panelDateInputChange();
    },
    autoComplete: "off",
    disabled: isDisabled(),
    readOnly: isDisabled(),
    maxLength: timeInputMaxLength
  }))))));
}

export default DatetimePanel;